import { inject, Injectable } from "@angular/core";
import { ExceptionsService } from "@core-services/exceptions.service";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { EstimateSwapPricesGQLService, EstimateSwapPricesInput, EstimateSwapPricesQuery } from "@gql/offer";
import { catchError, map, Observable } from "rxjs";
import { Wallet } from "@models";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { AccountService } from "@modules/account/services/account.service";

@Injectable({
	providedIn: "root"
})
export class TradeSwapFacade {
	private statsigService = inject(StatsigClientService);
	private estimateSwapPricesGQLService = inject(EstimateSwapPricesGQLService);
	private exceptionsService = inject(ExceptionsService);
	private tradableListService = inject(TradableListService);
	private accountService = inject(AccountService);

	isSwapEstimateGQLEnabled$ = this.statsigService.isFeatureActive(FEAT_GATES.SWAP_ESTIMATE_GQL_ENABLED);

	getSwapEstimateGQL$(input: EstimateSwapPricesInput): Observable<SwapDefaultPricesModel> {
		return this.estimateSwapPricesGQLService.fetch({ input }, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { estimateSwapPrices } }) => this.estimateGQLParser(estimateSwapPrices)),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	getSourceTargetPid(source: Wallet, target: Wallet): EstimateSwapPricesInput {
		const sourceAssetId = this.tradableListService.getItemById(source.getCurrencyId()).getPid();
		const targetAssetId = this.tradableListService.getItemById(target.getCurrencyId()).getPid();
		const sourceWalletId = source.getId();
		const fiatId = this.accountService.getDefaultFiat().getPid();

		return {
			sourceAssetId,
			targetAssetId,
			sourceWalletId,
			fiatId
		};
	}

	private estimateGQLParser(data: EstimateSwapPricesQuery["estimateSwapPrices"]): SwapDefaultPricesModel {
		const { target, source, userJourney, appropriatenessTest } = data;

		const model = new SwapDefaultPricesModel();
		model.setAppTestTradingWarning(appropriatenessTest.warning);
		model.setUserJourney(userJourney as unknown as UserJourney);
		model.source.minimum.source_amount = source.min.asset;
		model.source.minimum.fiat_amount = source.min.fiat;
		model.source.minimum.target_amount = target.forMinSource;
		model.source.wallet.source_amount = source.max.asset;
		model.source.wallet.fiat_amount = source.max.fiat;
		model.source.wallet.target_amount = target.forMaxSource;
		model.source.unit.source_amount = source.toTargetRatio;
		model.source.unit.target_amount = target.toSourceRatio;

		return model;
	}
}

/*
	API response model
*/

export class SwapDefaultPricesModel {
	public source: SwapEntity = new SwapEntity();
	public target: SwapEntity = new SwapEntity();
	private app_test_trading_warning = false;
	private user_journey: UserJourney;

	getAppTestTradingWarning(): boolean {
		return this.app_test_trading_warning;
	}

	getUserJourney(): UserJourney {
		return this.user_journey;
	}

	setUserJourney(journey: UserJourney): void {
		this.user_journey = journey;
	}

	setAppTestTradingWarning(warning: boolean): void {
		this.app_test_trading_warning = warning;
	}
}

export interface ExternalLauchOption {
	id?: string;
	id2?: string;
	wallet?: TradableWallet;
	usedFor?: string; // Left / right
	wallet2?: TradableWallet;
	usedFor2?: string;
}

class SwapEntity {
	public minimum: SwapDualPair = new SwapDualPair();
	public unit: SwapDualPair = new SwapDualPair();
	public unit_fiat: SwapDualPair = new SwapDualPair();
	public wallet: SwapDualPair = new SwapDualPair();
}

class SwapDualPair {
	public source_amount: string = "";
	public target_amount: string = "";
	public fiat_amount: string = "";
}
